import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Bot } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArticleDialog, ROLE_OPTS } from "@/components/assistant/ArticleDialog";
import { AssistantStats, UnansweredList } from "@/components/assistant/AssistantStats";

const ROLE_PT = Object.fromEntries(ROLE_OPTS);
const fmt = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");

function KnowledgeBase() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState({ open: false, article: null });
  const load = () => api.get("/assistant/admin/kb").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const remove = async (a) => {
    if (!window.confirm(`Excluir o tutorial "${a.title}"?`)) return;
    try { await api.delete(`/assistant/admin/kb/${a.id}`); toast.success("Tutorial excluído"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const list = rows.filter((a) => (a.title + " " + (a.menu || "")).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar tutorial..." className="max-w-xs" data-testid="kb-search-input" />
        <span className="text-xs text-slate-400">{rows.length} tutoriais · {rows.filter((a) => a.published).length} publicados</span>
        <Button onClick={() => setEdit({ open: true, article: null })} className="ml-auto bg-blue-700 hover:bg-blue-800" data-testid="kb-new-btn"><Plus className="mr-1 h-4 w-4" />Novo tutorial</Button>
      </div>
      <Card className="divide-y divide-slate-100" data-testid="kb-list">
        {list.map((a) => (
          <div key={a.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center" data-testid={`kb-row-${a.slug}`}>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-800">{a.title}</span>
                <Badge variant="outline" className={a.published ? "border-green-200 text-green-700" : "border-slate-200 text-slate-500"}>{a.published ? "Publicado" : "Rascunho"}</Badge>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{a.menu} · {(a.roles || []).map((r) => ROLE_PT[r] || r).join(", ")}</p>
              <p className="text-[11px] text-slate-400">Atualizado em {fmt(a.updated_at)}{a.updated_by ? ` por ${a.updated_by}` : ""}</p>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setEdit({ open: true, article: a })} data-testid={`kb-edit-${a.slug}`}><Pencil className="mr-1 h-3.5 w-3.5" />Editar</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(a)} className="text-red-600" data-testid={`kb-delete-${a.slug}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </Card>
      <ArticleDialog open={edit.open} article={edit.article} onClose={() => setEdit({ open: false, article: null })}
        onSaved={() => { setEdit({ open: false, article: null }); load(); }} />
    </div>
  );
}

export default function TreinamentoIA() {
  const [enabled, setEnabled] = useState(null);
  useEffect(() => { api.get("/assistant/status").then(({ data }) => setEnabled(data.enabled)); }, []);
  const toggle = async (v) => {
    try { await api.put("/assistant/admin/settings", { enabled: v }); setEnabled(v); toast.success(v ? "Acompanha IA ativado" : "Acompanha IA desativado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5" data-testid="treinamento-ia-page">
      <PageHeader title="Treinamento — Acompanha IA" subtitle="Base de conhecimento do assistente de treinamento, uso e dúvidas sem resposta">
        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium">
          <Bot className="h-4 w-4 text-orange-500" />{enabled ? "Assistente ativo" : "Assistente desativado"}
          <Switch checked={!!enabled} disabled={enabled === null} onCheckedChange={toggle} data-testid="assistant-enabled-switch" />
        </label>
      </PageHeader>
      <Tabs defaultValue="kb">
        <TabsList className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="kb" data-testid="tab-kb">Base de conhecimento</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Estatísticas</TabsTrigger>
          <TabsTrigger value="unanswered" data-testid="tab-unanswered">Sem resposta</TabsTrigger>
        </TabsList>
        <TabsContent value="kb" className="mt-4"><KnowledgeBase /></TabsContent>
        <TabsContent value="stats" className="mt-4"><AssistantStats /></TabsContent>
        <TabsContent value="unanswered" className="mt-4"><UnansweredList /></TabsContent>
      </Tabs>
    </div>
  );
}
